import { createReducer } from '@reduxjs/toolkit';
import { ViewerShape } from './model';
import * as viewerActions from './actions';
import { applyTotal, CACHE_MAX_ENTRIES, EMPTY_TOTAL, Loading } from '../finder/model';
import * as lastStateActions from '../lastState/actions';
import * as appActions from '../app/actions';

const initial: ViewerShape = {
  data: { assets: [], total: EMPTY_TOTAL },
  loading: Loading.none,
  hasNextPage: false,
  scrollHash: '',
  creative: undefined,
  cache: {},
};

export const viewerReducer = createReducer<ViewerShape>(initial, (builder) =>
  builder
    .addCase(viewerActions.setLoading, (s, a) => {
      s.loading = a.payload.loading;
    })
    .addCase(viewerActions.assetsChanged, (s, a) => {
      s.data.assets = a.payload.data.assets;
      applyTotal(s.data.total, a.payload.data.total);

      s.hasNextPage = a.payload.hasNextPage;
      s.scrollHash = a.payload.scrollHash;
    })
    .addCase(viewerActions.cacheHydrated, (s, a) => {
      // Гидрация из localStorage при старте. Не затираем записи, уже загруженные
      // в этой сессии (они свежее), — только добавляем отсутствующие.
      Object.entries(a.payload.cache).forEach(([key, entry]) => {
        if (!(key in s.cache)) {
          s.cache[key] = entry;
        }
      });
    })
    .addCase(viewerActions.cacheStored, (s, a) => {
      // Вытесняем по давности обращения, а не вставки: сначала удаляем ключ, потом
      // добавляем заново, поэтому он оказывается в конце (у строковых ключей порядок в
      // объекте — порядок вставки). Иначе локация, в которую постоянно возвращаются,
      // могла быть вытеснена случайным одноразовым заходом.
      delete s.cache[a.payload.key];
      if (Object.keys(s.cache).length >= CACHE_MAX_ENTRIES) {
        const oldestKey = Object.keys(s.cache)[0];
        if (oldestKey !== undefined) {
          delete s.cache[oldestKey];
        }
      }
      // Время записи ставим здесь, чтобы у кэша в памяти и у персиста был один источник
      // (по нему считается устаревание, см. isCacheStale).
      s.cache[a.payload.key] = { ...a.payload.entry, storedAt: Date.now() };
    })
    .addCase(viewerActions.cacheInvalidated, (s, a) => {
      delete s.cache[a.payload.key];
    })
    .addCase(viewerActions.thumbnailUpdated, (s, a) => {
      s.data.assets = s.data.assets.map((asset) =>
        asset.id === a.payload.assetId ? { ...asset, thumbnail_url: a.payload.url } : asset,
      );
    })
    .addCase(viewerActions.creativeChanged, (s, a) => {
      s.creative = a.payload.creative;
    })
    .addCase(lastStateActions.applied, (s, a) => {
      s.creative = a.payload?.state.data.viewer?.creative ?? initial.creative;
    })
    .addCase(appActions.resetFilters, (s) => {
      s.creative = undefined;
    }),
);
